import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isSupportedLocale, type Locale } from '@boletra/i18n';
import { DashboardShell } from '../../components/DashboardShell.js';
import { BannerUploader } from '../../components/BannerUploader.js';
import { RichTextEditor } from '../../components/RichTextEditor.js';
import { api, ApiClientError } from '../../lib/api.js';
import { useCurrentOrg, useOrgDetails } from '../../lib/orgs.js';
import { useAuthStore } from '../../lib/auth.js';
import { OnboardingPage } from './OnboardingPage.js';

interface CreateEventResponse {
  id: string;
}

function slugify(input: string): string {
  return input
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 50);
}

const COMMON_TZ = [
  'America/Mexico_City',
  'America/Monterrey',
  'America/Tijuana',
  'America/Cancun',
  'America/Bogota',
  'America/Lima',
  'America/Santiago',
  'America/Buenos_Aires',
  'America/Guayaquil',
  'America/New_York',
  'America/Los_Angeles',
  'Europe/Madrid',
  'UTC',
];

export function CreateEventPage() {
  const { t, i18n } = useTranslation('dashboard');
  const { lang } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const org = useCurrentOrg();
  const orgDetails = useOrgDetails(org?.id ?? null);
  const user = useAuthStore((s) => s.user);

  const today = new Date();
  const defaultStart = toLocalDatetimeInput(addDays(today, 14, 20));
  const defaultEnd = toLocalDatetimeInput(addDays(today, 14, 23));

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [touchedSlug, setTouchedSlug] = useState(false);
  const [description, setDescription] = useState('');
  const [venue, setVenue] = useState('');
  const [startsAt, setStartsAt] = useState(defaultStart);
  const [endsAt, setEndsAt] = useState(defaultEnd);
  const [timezone, setTimezone] = useState(getDefaultTimezone());
  const [serviceFeePct, setServiceFeePct] = useState(0);
  const [bannerUrl, setBannerUrl] = useState('');

  // La moneda viene del país de la org (default settlement currency de su
  // cuenta Stripe Connect). El server la fija sin importar lo que enviemos.
  const currency = orgDetails.data?.settings.defaultCurrency ?? '';

  // El idioma del evento se hereda del idioma que el productor está usando
  // ahora mismo. La URL es la fuente confiable (`/es/...` o `/en/...` —
  // `LocaleGuard` la sincroniza con `i18n.language`); el body del request
  // valida estrictamente contra `localeSchema` así que no podemos enviar
  // variantes regionales como `'en-US'` ni nada raro.
  const effectiveLocale: Locale = pickLocale(lang, i18n.language);

  const mutation = useMutation({
    mutationFn: () => {
      const effectiveSlug = touchedSlug ? slug : slugify(title);
      return api<CreateEventResponse>('/events', {
        method: 'POST',
        body: {
          orgId: org!.id,
          slug: effectiveSlug,
          startsAt: new Date(startsAt).toISOString(),
          endsAt: new Date(endsAt).toISOString(),
          timezone,
          defaultLocale: effectiveLocale,
          supportedLocales: [effectiveLocale],
          translations: {
            [effectiveLocale]: {
              title: title.trim(),
              description: description.trim(),
              venueLabel: venue.trim(),
            },
          },
          serviceFeePct,
          bannerUrl: bannerUrl || undefined,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgs', org!.id, 'events'] });
      navigate(`/${lang}/dashboard/events`, { replace: true });
    },
  });

  if (!org) return <OnboardingPage />;

  if (!user?.emailVerifiedAt) {
    return (
      <DashboardShell>
        <div className="max-w-md mx-auto py-20 text-center px-4">
          <p className="text-neutral-300 mb-6">{t('email_verification.create_event_blocked')}</p>
          <a href={`/${lang}/dashboard`} className="btn-primary">
            {t('email_verification.create_event_blocked_cta')}
          </a>
        </div>
      </DashboardShell>
    );
  }

  const effectiveSlug = touchedSlug ? slug : slugify(title);
  const canSubmit =
    title.trim().length >= 2 &&
    effectiveSlug.length >= 2 &&
    startsAt &&
    endsAt &&
    new Date(endsAt) > new Date(startsAt) &&
    Boolean(currency) &&
    !mutation.isPending;

  return (
    <DashboardShell>
      <h1 className="text-3xl font-bold mb-2">{t('create_event.title')}</h1>
      <p className="text-neutral-400 mb-8 text-sm">{t('create_event.subtitle')}</p>

      <form
        className="space-y-5 max-w-2xl"
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSubmit) return;
          mutation.mutate();
        }}
      >
        <Field label={t('create_event.title_label')}>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            minLength={2}
            maxLength={160}
            placeholder={t('create_event.title_placeholder')}
            className="input"
          />
        </Field>

        <Field label={t('create_event.slug_label')} hint={t('create_event.slug_help')}>
          <input
            type="text"
            value={effectiveSlug}
            onChange={(e) => {
              setTouchedSlug(true);
              setSlug(slugify(e.target.value));
            }}
            pattern="[a-z0-9-]+"
            required
            minLength={2}
            maxLength={50}
            className="input"
          />
        </Field>

        <Field label={t('create_event.description_label')}>
          <RichTextEditor
            value={description}
            onChange={setDescription}
            rows={4}
            maxLength={8000}
            placeholder={t('create_event.description_placeholder')}
            labels={{
              bold: t('event_editor.rich_text.bold'),
              italic: t('event_editor.rich_text.italic'),
              hint: t('event_editor.rich_text.hint'),
            }}
          />
        </Field>

        <Field label={t('create_event.venue_label')}>
          <input
            type="text"
            value={venue}
            onChange={(e) => setVenue(e.target.value)}
            maxLength={160}
            placeholder={t('create_event.venue_placeholder')}
            className="input"
          />
        </Field>

        <Field label={t('create_event.banner_label')} hint={t('create_event.banner_help')}>
          <BannerUploader orgId={org.id} value={bannerUrl} onChange={setBannerUrl} />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t('create_event.starts_at_label')}>
            <input
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              required
              className="input"
            />
          </Field>
          <Field label={t('create_event.ends_at_label')}>
            <input
              type="datetime-local"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              required
              className="input"
            />
          </Field>
        </div>

        <Field label={t('create_event.timezone_label')}>
          <select value={timezone} onChange={(e) => setTimezone(e.target.value)} className="input">
            {COMMON_TZ.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t('create_event.currency_label')} hint={t('create_event.currency_help')}>
            <input type="text" value={currency} readOnly disabled className="input opacity-60" />
          </Field>
          <Field
            label={t('create_event.service_fee_label')}
            hint={t('create_event.service_fee_help')}
          >
            <input
              type="number"
              min={0}
              max={50}
              step={0.5}
              value={serviceFeePct}
              onChange={(e) => setServiceFeePct(Number(e.target.value))}
              className="input"
            />
          </Field>
        </div>

        <div className="flex flex-wrap gap-3 pt-2">
          <button type="submit" disabled={!canSubmit} className="btn-primary">
            {mutation.isPending ? t('create_event.creating') : t('create_event.create_button')}
          </button>
          <button
            type="button"
            onClick={() => navigate(`/${lang}/dashboard/events`)}
            className="btn-secondary"
          >
            {t('create_event.cancel_button')}
          </button>
        </div>
        {mutation.isError && (
          <p className="text-red-400 text-sm">{(mutation.error as ApiClientError).message}</p>
        )}
      </form>
    </DashboardShell>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-sm text-neutral-400">{label}</span>
      <div className="mt-2">{children}</div>
      {hint && <p className="text-xs text-neutral-500 mt-1">{hint}</p>}
    </label>
  );
}

function addDays(d: Date, days: number, hour = d.getHours()): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + days);
  out.setHours(hour, 0, 0, 0);
  return out;
}

function toLocalDatetimeInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function getDefaultTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (COMMON_TZ.includes(tz)) return tz;
  } catch {
    /* noop */
  }
  return 'America/Mexico_City';
}

/**
 * Resuelve el locale efectivo para crear el evento. Probamos en orden:
 *   1. El `:lang` del path (fuente de verdad: el usuario lo eligió al
 *      navegar — `/en/...` o `/es/...`).
 *   2. `i18n.language` (puede llegar como `'en-US'`, `'es-419'`, etc.; lo
 *      cortamos al base BCP-47).
 *   3. Fallback `'es'` (default histórico de la plataforma).
 *
 * Si en algún punto encontramos un código soportado, lo devolvemos.
 * Importante: la URL casi siempre gana porque `LocaleGuard` rebota a
 * `/es/...` cuando el lang no es soportado.
 */
function pickLocale(...candidates: Array<string | undefined>): Locale {
  for (const raw of candidates) {
    if (!raw) continue;
    const base = raw.split('-')[0]?.toLowerCase();
    if (base && isSupportedLocale(base)) return base;
  }
  return 'es';
}
